import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';
import DOMPurify from 'dompurify';
import api from '../services/api';

interface PolicySection {
  id: string;
  title: string;
  field: string;
}

const POLICY_SECTIONS: PolicySection[] = [
  { id: 'data_privacy', title: 'Data Privacy Policy', field: 'data_privacy_policy' },
  { id: 'photo', title: 'Photo/Video Release', field: 'photo_video_release_policy' },
  { id: 'health', title: 'Health & Safety Policy', field: 'health_safety_policy' },
  { id: 'conduct', title: 'Code of Conduct', field: 'code_of_conduct_policy' },
  { id: 'enrollment', title: 'Enrollment & Cancellation Policy', field: 'enrollment_cancellation_policy' },
  { id: 'liability', title: 'Liability & Waivers', field: 'liability_waiver_policy' },
  { id: 'property', title: 'Property & Equipment', field: 'property_equipment_policy' },
  { id: 'ip', title: 'Intellectual Property', field: 'intellectual_property_policy' },
];

const PrivacyPolicyPage: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>('data_privacy');

  const { data: settings, isLoading } = useQuery('siteSettings', async () => {
    try {
      const response = await api.get('/cms/settings/');
      return response.data;
    } catch {
      return null;
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const hasSeparatePolicies = POLICY_SECTIONS.some(s => settings?.[s.field]);

  return (
    <div className="pt-28 min-h-screen bg-gray-50">
      <section className="gradient-bg py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <Shield className="mx-auto mb-6" size={64} />
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">Privacy Policy</h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Your privacy is important to us
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container-custom max-w-4xl">
          {hasSeparatePolicies ? (
            <div className="space-y-4">
              {POLICY_SECTIONS.map((section) => {
                const content = settings?.[section.field];
                if (!content) return null;
                const isExpanded = expandedSection === section.id;
                return (
                  <motion.div
                    key={section.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-md overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                    >
                      <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
                      {isExpanded ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
                    </button>
                    {isExpanded && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div
                          className="prose prose-sm max-w-none mt-4"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
                        />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-md p-8 md:p-12"
            >
              {settings?.privacy_policy ? (
                <div
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(settings.privacy_policy) }}
                />
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600 text-lg">
                    Privacy Policy content is being updated. Please check back soon.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
